import { Chip } from '@mui/material'
import { useContext, useState } from 'react'
import { ICell } from '../../../../../../../app/entities/Cell'
import styles from './styles.module.css'
import { UserContext } from '../../../../../base/components/base-container/BaseContainer'
import hasPermission from '../../../../../global/utils/user/has-permission'
import { PERMISSIONS } from '../../../../../global/constants/permissions'
import { ILocation } from '../../../../../../../app/entities/Location'

interface ICellsManagerProps {
  locationFormInfo: Partial<ILocation>
  setLocationFormInfo: Function
}

export default function CellsManager({
  locationFormInfo,
  setLocationFormInfo,
}: ICellsManagerProps) {
  const { objectId: locationObjectId = '', cells = [] } = locationFormInfo

  const { user } = useContext(UserContext)
  const [cellInputBackgroundColor, setCellInputBackgroundColor] =
    useState('#f5f5f5')
  const [cellInputValue, setCellInputValue] = useState('')

  const cellNames = cells ? cells.map((cell) => cell.name).sort() : []

  const canEditNewCells = hasPermission(user).edit(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.CELLS.NAME,
  )
  const canViewExistingCells = hasPermission(user).view(
    PERMISSIONS.LOCATIONS.CATEGORY,
    PERMISSIONS.LOCATIONS.ENTRIES.CELLS.NAME,
  )

  const inputChip = canEditNewCells ? (
    <Chip
      key='input-chip'
      style={{
        border: '1px solid #2a7187',
        cursor: canEditNewCells ? 'text' : undefined,
        backgroundColor: cellInputBackgroundColor,
        boxShadow: '0 0 .25rem 0 rgba(0,0,0,.25) inset',
      }}
      label={
        <input
          placeholder='Add Cell'
          style={{
            all: 'unset',
            width: '3.5rem',
            userSelect: 'none',
          }}
          value={cellInputValue}
          disabled={!canEditNewCells}
          autoComplete='off'
          onPaste={(e) => {
            if (!canEditNewCells) return
            const cellsString = e.clipboardData.getData('text/plain')

            const goodCells = cellsString
              .split('\n')
              .map((cell) => cell.trim().toUpperCase())
              .filter((cell) => /^[A-Z]\d[A-Z]$/.test(cell))
              .filter(
                (cell) =>
                  !cells.find((existingCell) => existingCell.name === cell),
              )

            setLocationFormInfo({
              ...locationFormInfo,
              cells: [...cells, ...goodCells.map((name) => ({ name }))],
            })

            setTimeout(() => {
              setCellInputValue('')
            }, 1)
          }}
          onChange={(e) => {
            setCellInputBackgroundColor('#f5f5f5')
            setCellInputValue(e.target.value.toUpperCase())
          }}
          onKeyDown={handleKeyDown}
        />
      }
    />
  ) : null

  async function removeCell(cellName: string) {
    if (!canEditNewCells) return
    try {
      setLocationFormInfo({
        ...locationFormInfo,
        cells: cells.filter((cell) => cell.name !== cellName),
      })
    } catch (error) {
      console.error(error)
    }
  }

  const cellChips = cellNames.map((cellName, idx) => (
    <Chip
      key={cellName + '-' + idx}
      label={cellName}
      style={{
        backgroundColor: '#2a7187',
        color: 'white',
        fontWeight: 'bold',
      }}
      disabled={!canEditNewCells}
      onDelete={() => removeCell(cellName)}
    />
  ))

  async function handleKeyDown(e: any) {
    if (e.key === 'Enter') {
      const newCellName = e.currentTarget.value.toUpperCase()
      if (newCellName && /^[A-Z]\d[A-Z]$/.test(newCellName)) {
        if (cells.find((cell) => cell.name === newCellName)) {
          setCellInputBackgroundColor('#fcebeb')
        } else {
          try {
            setLocationFormInfo({
              ...locationFormInfo,
              cells: [...cells, { name: newCellName }],
            })
            setCellInputValue('')
          } catch (error) {
            console.error(error)
            setCellInputBackgroundColor('#fcebeb')
          }
        }
      }
    }
  }

  return (
    <>
      {canViewExistingCells && (
        <div className={styles.cellsManagerMain}>
          <div className={styles.cellsManagerContainer}>
            <div className={styles.cellsManagerHeader}>
              <div className={`${styles.cellsManagerTitle} font--bold`}>
                Storage Cells
              </div>
              <div className={styles.cellsManagerSubtitle}>
                Format: Row Letter + Floor Number + Bin Letter (e.g., A1A, B2C)
              </div>
            </div>
            <div className={styles.cellsManagerChipsContainer}>
              {inputChip}
              {cellChips}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
